import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreatePro013CampaignMutation,
  useGetPro013CampaignByCodeQuery,
  useUpdatePro013CampaignMutation
} from "@/services/campaign";
import { toast } from "@/components/ui/use-toast";
import { CampaignType } from "@/constants/campaign-types";
import { CampaignConflictModal } from "../../components/campaign-conflict-modal";
import { getTranslatedErrorMessage } from "../../utils/error-messages";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { CampaignStoresEditor } from "../../components/edit-campaign/campaign-stores-editor";
import StepIndicator from "./step-indicator";
import { CampaignPreviewHeader } from "./campaign-preview-header";
import StepOnePro013 from "./steps-pro013/step-one";
import StepTwoStores from "./steps-pro013/step-two-stores";
import StepThreeDiscount from "./steps-pro013/step-three-discount";
import StepFourProducts from "./steps-pro013/step-four-products";
import StepFiveReview from "./steps-pro013/step-five-review";

interface CampaignConflictItem {
  itemCode: string;
  itemName: string;
  conflictingCampaigns: Array<{
    code: number;
    description: string;
    type: string;
    startDate: string;
    endDate: string;
    storeCode: number;
  }>;
}

export interface Pro013CampaignData {
  // Step 1: Informações Gerais
  description: string;
  startDate: string;
  endDate: string;
  allStores: boolean;
  stores: number[];

  // Step 2: Configuração de Desconto
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: number; // P13TIPO (0-8 exceto 6)
  allStoreFlag: number; // P13TODALJ (0=não, 1=sim)
  paymentMethod: number; // P13MOEDA (0=todas)

  // Step 3: Departamentos ou Grupos
  departments: number[];
  groups: number[];
}

interface CreatePro013FormProps {
  onChangeType?: (type: CampaignType) => void;
  mode?: 'create' | 'edit';
  campaignCode?: number;
}

export function CreatePro013Form({ onChangeType, mode = 'create', campaignCode }: CreatePro013FormProps) {
  const navigate = useNavigate();
  const [createCampaign, { isLoading: isCreating }] = useCreatePro013CampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdatePro013CampaignMutation();

  // Fetch campaign data in edit mode
  const { data: existingCampaign, isLoading: isLoadingCampaign } = useGetPro013CampaignByCodeQuery(
    campaignCode!,
    { skip: mode !== 'edit' || !campaignCode }
  );

  const isLoading = isCreating || isUpdating;
  const [currentStep, setCurrentStep] = useState(1);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [campaignData, setCampaignData] = useState<Pro013CampaignData>({
    description: "",
    startDate: "",
    endDate: "",
    allStores: true,
    stores: [],
    takeQuantity: 2,
    payQuantity: 1,
    discountValue: 0,
    discountType: 0,
    allStoreFlag: 0,
    paymentMethod: 0,
    departments: [],
    groups: [],
  });

  // PRO013 sempre tem 5 steps, mas step 4 (Produtos) é bloqueado quando allStoreFlag=1
  const totalSteps = 5;

  // Steps customizados para PRO013
  const pro013Steps = [
    { number: 1, title: "Período" },
    { number: 2, title: "Lojas" },
    { number: 3, title: "Desconto" },
    { number: 4, title: "Produtos" },
    { number: 5, title: "Revisão" },
  ];

  // Pre-populate data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && existingCampaign?.data) {
      const campaign = existingCampaign.data;
      setCampaignData({
        description: campaign.description || "",
        startDate: campaign.startDate || "",
        endDate: campaign.endDate || "",
        allStores: campaign.allStoreFlag === 1 || campaign.storeCode === 999,
        stores: campaign.stores || [],
        takeQuantity: campaign.takeQuantity || 2,
        payQuantity: campaign.payQuantity || 1,
        discountValue: campaign.discountValue || 0,
        discountType: campaign.discountType || 0,
        allStoreFlag: campaign.allStoreFlag || 0,
        paymentMethod: campaign.paymentMethod || 0,
        departments: campaign.departments || [],
        groups: campaign.groups || [],
      });
    }
  }, [mode, existingCampaign]);

  // Quando allStoreFlag = 1, zerar departamentos e grupos automaticamente
  useEffect(() => {
    if (campaignData.allStoreFlag === 1) {
      setCampaignData(prev => ({
        ...prev,
        departments: [],
        groups: [],
      }));
    }
  }, [campaignData.allStoreFlag]);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      // Se allStoreFlag=1 e está no step 3, pular para step 5 (Revisão)
      if (campaignData.allStoreFlag === 1 && currentStep === 3) {
        setCurrentStep(5);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // Se allStoreFlag=1 e está no step 5, voltar para step 3 (pular step 4)
      if (campaignData.allStoreFlag === 1 && currentStep === 5) {
        setCurrentStep(3);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const updateData = (data: Partial<Pro013CampaignData>) => {
    setCampaignData({ ...campaignData, ...data });
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        localStorage.removeItem('campaign_pro013_draft');

        // Quando allStoreFlag=1 (toda a loja), não envia departamentos/grupos
        const payload = {
          description: campaignData.description,
          startDate: campaignData.startDate,
          endDate: campaignData.endDate,
          storeCode: campaignData.allStores ? 999 : undefined,
          stores: campaignData.allStores ? [] : campaignData.stores,
          takeQuantity: campaignData.takeQuantity,
          payQuantity: campaignData.payQuantity,
          discountValue: campaignData.discountValue,
          discountType: campaignData.discountType,
          allStoreFlag: campaignData.allStoreFlag,
          paymentMethod: campaignData.paymentMethod,
          departments: campaignData.allStoreFlag === 1 ? [] : campaignData.departments,
          groups: campaignData.allStoreFlag === 1 ? [] : campaignData.groups,
        };

        console.log('Payload PRO013 (CREATE):', payload);

        const result = await createCampaign(payload).unwrap();

        if (result.data && "queued" in result.data) {
          toast({
            title: "Criação em segundo plano",
            description: `Campanha "${campaignData.description}" enviada para a fila (Job #${result.data.jobId}). Você será notificado ao concluir.`,
            variant: "default",
          });
          setTimeout(() => {
            navigate("/planejamento-comercial/gestao-campanhas");
          }, 1500);
          return;
        }

        toast({
          title: "Sucesso!",
          description: `Campanha "${campaignData.description}" #${result.data?.code} criada com sucesso!`,
          variant: "default",
        });

        setTimeout(() => {
          navigate("/planejamento-comercial/gestao-campanhas");
        }, 1500);
      } else {
        // Edit mode - call UPDATE endpoint
        const updatePayload = {
          code: campaignCode!,
          description: campaignData.description,
          startDate: campaignData.startDate,
          endDate: campaignData.endDate,
          departments: campaignData.departments,
          groups: campaignData.groups,
        };

        console.log('Payload PRO013 (UPDATE):', updatePayload);

        await updateCampaign(updatePayload).unwrap();

        toast({
          title: "Sucesso!",
          description: `Campanha "${campaignData.description}" #${campaignCode} atualizada com sucesso!`,
          variant: "default",
        });

        setTimeout(() => {
          navigate("/planejamento-comercial/gestao-campanhas");
        }, 1500);
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} PRO013 campaign:`, error);

      if (error?.status === 409 && error?.data?.conflicts) {
        setConflicts(error.data.conflicts);
        setShowConflictModal(true);
      } else {
        const errorMessage = getTranslatedErrorMessage(
          error,
          `Erro ao ${mode === 'create' ? 'criar' : 'atualizar'} campanha PRO013. Verifique os dados e tente novamente.`
        );

        toast({
          title: "Erro",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleCloseConflictModal = () => {
    setShowConflictModal(false);
    setConflicts([]);
  };

  const handleGoBackFromConflict = () => {
    setShowConflictModal(false);
    setCurrentStep(1);
  };

  const handleNavigateToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  if (isLoadingCampaign) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando dados da campanha...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {mode === 'edit' && campaignCode && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Modo Edição - Campanha PRO013 #{campaignCode}
          </AlertDescription>
        </Alert>
      )}


      <StepIndicator
        currentStep={currentStep}
        totalSteps={totalSteps}
        steps={pro013Steps}
        onStepClick={handleNavigateToStep}
        disabledSteps={campaignData.allStoreFlag === 1 ? [4] : []}
        disabledStepMessage="Etapa de produtos não disponível quando 'Toda a Loja' está selecionado"
        requireConfirmationFrom={2}
      >
        <CampaignPreviewHeader
          campaignType="PRO013"
          description={campaignData.description}
          startDate={campaignData.startDate}
          endDate={campaignData.endDate}
          takeQuantity={campaignData.takeQuantity}
          payQuantity={campaignData.payQuantity}
          discountValue={campaignData.discountValue}
          discountType={campaignData.discountType}
          productsCount={campaignData.departments.length + campaignData.groups.length}
        />
      </StepIndicator>

      <div className="mt-4">
        {currentStep === 1 && (
          <StepOnePro013
            data={campaignData}
            updateData={updateData}
            nextStep={nextStep}
            onChangeType={onChangeType}
          />
        )}
        {/* Edição: lojas são salvas na hora via PATCH /stores (CampaignStoresEditor) —
            o submit do wizard NÃO envia lojas (antes eram silenciosamente descartadas). */}
        {currentStep === 2 && mode === "edit" && campaignCode ? (
          <div className="space-y-4 animate-in fade-in duration-300">
            {/* allStores={false}: campaignData.allStores conflita allStoreFlag
                (escopo de desconto) com global; o editor detecta o 999 na
                própria lista de lojas (hasGlobalSentinel). */}
            <CampaignStoresEditor
              type="PRO013"
              campaignCode={campaignCode}
              currentStores={existingCampaign?.data?.stores || []}
              allStores={false}
              status={existingCampaign?.data?.status || "ACTIVE"}
            />
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={prevStep} className="w-full md:w-auto">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
              <Button onClick={nextStep} className="w-full md:w-auto">
                Continuar
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : currentStep === 2 ? (
          <StepTwoStores
            data={campaignData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        ) : null}
        {currentStep === 3 && (
          <StepThreeDiscount
            data={campaignData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}
        {/* Step 4: Produtos - só aparece se allStoreFlag != 1 */}
        {currentStep === 4 && campaignData.allStoreFlag !== 1 && (
          <StepFourProducts
            data={campaignData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}
        {/* Step 5: Revisão */}
        {currentStep === 5 && (
          <StepFiveReview
            data={campaignData}
            prevStep={prevStep}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            mode={mode}
          />
        )}
      </div>

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={handleCloseConflictModal}
        onGoBack={handleGoBackFromConflict}
      />
    </div>
  );
}
